// === cart-drawer.js (stable + sync + smooth + scroll + coalesced refresh) ===
const ENABLE_DRAWER_HIGHLIGHT = false; // set true for blue outline on the just-added row

// Throttle consecutive refreshes so we don't double-swap the drawer
const REFRESH_THROTTLE_MS = 900; // tweak to 1200 if you still see bounce

class CartDrawer extends HTMLElement {

  // Track last-known states to avoid unnecessary full swaps
__lastIsEmpty = null;
__lastOpenAt = 0;

  constructor() {
    super();

    this.__refreshing = false;
    this.__pendingRefresh = null;
    this.__lastRefreshAt = 0;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    // Cross-tab: refresh when other tabs mark the cart dirty/tick
    window.addEventListener('storage', (e) => {
      if (!e || (e.key !== 'cart:dirty' && e.key !== 'cart:tick')) {
        return;
      }

      // Use the theme's global refresh trigger, which updates all components.
      // This function is created by the bridge in cart.js.
      if (typeof window.__triggerCartRefreshFromTheme === 'function') {
        window.__triggerCartRefreshFromTheme('storage-event');
      } else {
        // Fallback to old behavior if the global function isn't available for some reason.
        console.warn('[CartDrawer] Global refresh trigger not found, falling back to drawer-only refresh.');
        if (Date.now() - this.__lastRefreshAt < REFRESH_THROTTLE_MS) return;
        const ts = parseInt(e.newValue || '0', 10) || 0;
        if (ts && ts <= this.__lastRefreshAt) return;
        this.refreshFromServer('storage');
      }
    });

    // Also refresh this drawer when cart updates in this tab (if pubsub exists)
    try {
      subscribe && subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        try { localStorage.setItem('cart:dirty', String(Date.now())); } catch(e) {}
        this.refreshFromServer('pubsub');
      });
    } catch (e) {
      // pubsub not present on some templates — ok
    }

    // Re-sync quickly on focus/visibility return
    window.addEventListener('focus', () => this.refreshFromServer('focus'));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refreshFromServer('visibility');
    });

    // Remember the last-added variant; used for scroll targeting after render
    document.addEventListener('theme:cart:item-added', (e) => {
      this.__pendingVariantId = String(e?.detail?.variantId || '');
    });

    this.setHeaderCartIconAccessibility();

    // Small helper CSS (highlight + smoother swaps)
    if (!document.getElementById('cart-drawer-helper-style')) {
      const s = document.createElement('style');
      s.id = 'cart-drawer-helper-style';
      s.textContent = `
        ${ENABLE_DRAWER_HIGHLIGHT ? '.cart-item--just-added{outline:2px solid #00a3e0;outline-offset:2px;transition:outline-color .6s}' : ''}
        .drawer__contents.is-swapping{will-change:contents}
      `;
      document.head.appendChild(s);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* ----------------- Helpers ----------------- */

  _computeCartSig(cartJson) {
    try {
      const items = (cartJson && cartJson.items) ? cartJson.items : [];
      const key = items.map(it => `${it.id}x${it.quantity}`).join(',');
      const count = cartJson?.item_count || 0;
      const total = cartJson?.total_price || 0;
      return `${count}|${total}|${key}`;
    } catch (e) {
      return '';
    }
  }

  _freezeHeight(el) {
    if (!el) return () => {};
    const h = el.getBoundingClientRect().height;
    const prevMinH = el.style.minHeight || '';
    el.style.minHeight = `${Math.max(1, Math.floor(h))}px`;
    return () => { el.style.minHeight = prevMinH; };
  }

  _rebindDrawerInteractions() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
  }

  // Strongly sync the cart page visibility with known state.
  // hasAnyItemsPreferred: pass true/false if you already know from /cart.js.
  // If null, it falls back to DOM sniffing.
  // Strongly sync only the MAIN cart page UI (never the drawer)
  _syncMainCartEmptyUI(hasAnyItemsPreferred = null) {
    const notInDrawer = (el) => el && !el.closest('cart-drawer');

    // Target the real cart page form (exclude the drawer's CartDrawer-Form)
    const form = Array.from(document.querySelectorAll('form#cart, form.cart__contents'))
      .find(notInDrawer);

    // Cart page host element
    const host = Array.from(document.querySelectorAll('cart-items'))
      .find(notInDrawer) || document.querySelector('cart-items');

    // Cart page items container and empty-message blocks (exclude any drawer copies)
    const itemsContainer = document.querySelector('#main-cart-items'); // only on page
    const emptyBlocks = Array.from(document.querySelectorAll(
      '.cart__warnings, .cart--empty-message-container, .custom-main-cart-empty-message'
    )).filter(notInDrawer);

    // DOM guess (fallback) vs. known truth from /cart.js
    const domThinksHasItems = !!(itemsContainer && itemsContainer.querySelector('.cart-item'));
    const hasAnyItems = (hasAnyItemsPreferred !== null) ? !!hasAnyItemsPreferred : domThinksHasItems;

    // Toggle empty state on host + form
    if (host) host.classList.toggle('is-empty', !hasAnyItems);
    if (form) {
      form.classList.toggle('is-empty', !hasAnyItems);
      form.classList.remove('critical-hidden');
      form.style.removeProperty('display');     // ← clears stale inline display:none
    }

    // Show/hide the list vs. empty messages
    if (itemsContainer) itemsContainer.style.display = hasAnyItems ? '' : 'none';
    emptyBlocks.forEach(el => { el.style.display = hasAnyItems ? 'none' : ''; });
  }

  _coalesceStart() {
    const now = Date.now();
    if (this.__refreshing) { this.__pendingRefresh = true; return true; }
    if (now - this.__lastRefreshAt < REFRESH_THROTTLE_MS) return true; // was 400
    this.__refreshing = true;
    this.__lastRefreshAt = now;
    return false;
  }

  _coalesceEnd(reason) {
    const sigChanged = !!this.__sigChangedThisPass;
    this.__sigChangedThisPass = false;

    this.__refreshing = false;

    // Avoid ping-pong: only tick when we had a real cart change and we weren't triggered by storage
    if (sigChanged && reason !== 'storage' && Date.now() - (this.__lastTickAt || 0) > 1500) {
      try {
        this.__lastTickAt = Date.now();
        localStorage.setItem('cart:tick', String(this.__lastTickAt));
      } catch (e) {}
    }

    if (this.__pendingRefresh) {
      this.__pendingRefresh = false;
      setTimeout(() => this.refreshFromServer('coalesced'), 50);
    }
  }

  /* ----------------- Public API ----------------- */

  async refreshFromServer(reason = 'open') {
    if (this._coalesceStart()) return;

    // Ignore echoes from storage/focus/pubsub that happen right after an open
    if (reason !== 'open' && this.__justOpenedAt && (Date.now() - this.__justOpenedAt) < 700) {
      this._coalesceEnd(reason);
      return;
    }

    try {
      // 1) Fetch cart JSON to detect empty and bubble count
      const cartJson = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      }).then(r => r.json());
      const isEmpty = !cartJson || !cartJson.items || cartJson.items.length === 0;

      // NEW: compute signature and remember if it changed this pass
      const nextSig = this._computeCartSig(cartJson);
      const sigChanged = nextSig !== this.__lastSig;
      this.__sigChangedThisPass = sigChanged;

      // 2) Fetch drawer section HTML and swap the ENTIRE drawer root (covers empty -> first add)
      const drawerDoc = await fetch(`${routes.cart_url}?section_id=cart-drawer`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      })
        .then(r => r.text())
        .then(html => new DOMParser().parseFromString(html, 'text/html'));

      const srcRoot  = drawerDoc.querySelector('#CartDrawer');
      const dstRoot  = this.querySelector('#CartDrawer');

      const contents = this.querySelector('.drawer__contents');
      const unfreeze = this._freezeHeight(contents);

      /* --- SMART SWAP: only rebuild what changed, preserve scroll --- */
      const prevScrollTop = contents ? contents.scrollTop : 0;

      // Did the empty/non-empty state change since last time?
      const changedEmpty = (this.__lastIsEmpty !== isEmpty);
      this.__lastIsEmpty = isEmpty;

      // Helper to replace inner HTML only if different
      const smartReplaceInner = (selector) => {
        const dst = this.querySelector(selector);
        const src = drawerDoc.querySelector(selector);
        if (!dst || !src) return;
        const next = src.innerHTML.trim();
        if (dst.innerHTML.trim() !== next) dst.innerHTML = next;
      };

      if (srcRoot && dstRoot) {
        if (changedEmpty || !dstRoot.firstElementChild) {
          // Structure differs (empty ↔ non-empty). Swap entire drawer once.
          dstRoot.innerHTML = srcRoot.innerHTML;
          this._rebindDrawerInteractions();
        } else {
          // Same structure. Update only list + footer to avoid disconnect/reconnect churn.
          smartReplaceInner('#CartDrawer-CartItems'); // item list
          smartReplaceInner('.drawer__footer');       // totals & buttons
        }
      }

      // Restore scroll position in the list
      if (contents) contents.scrollTop = prevScrollTop;

      /* --- keep the rest (form + host empty-state classes) exactly as you had --- */
      // (sync the form's empty state with the real cart)
      const formEl = this.querySelector('#CartDrawer-Form');
      if (formEl) formEl.classList.toggle('is-empty', isEmpty);

      // Toggle empty/busy classes on the host
      this.classList.toggle('is-empty', isEmpty);
      this.querySelector('#CartDrawer')?.classList.toggle('is-empty', isEmpty);
      this.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);

      // 3) Update bubble (robust across different wrappers)
      const bubbleDoc = await fetch(`/?section_id=cart-icon-bubble`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      })
        .then(r => r.text())
        .then(html => new DOMParser().parseFromString(html, 'text/html'));

      const bubbleSrc = bubbleDoc.querySelector('.shopify-section');
      const bubbleDst = document.querySelector('#shopify-section-cart-icon-bubble, #cart-icon-bubble');

      if (bubbleSrc && bubbleDst) {
        bubbleDst.innerHTML = bubbleSrc.innerHTML;
      } else {
        // Fallback: update just the count
        try {
          const count = (cartJson && cartJson.item_count) || 0;
          const cntEl = document.querySelector(
            '#cart-icon-bubble .cart-count-bubble, .cart-count-bubble, [data-cart-count-bubble]'
          );
          if (cntEl) {
            if (count > 0) {
              cntEl.classList.remove('hidden');
              cntEl.textContent = String(count);
            } else {
              cntEl.classList.add('hidden');
              cntEl.textContent = '';
            }
          }
        } catch(e) {}
      }

      // 4) Keep the /cart page sections in sync when present
      /*
      const mainItemsEl = document.getElementById('main-cart-items');
      const footerEl    = document.getElementById('main-cart-footer');
      const pageSectionIds = [];
      if (mainItemsEl?.dataset?.id) pageSectionIds.push(mainItemsEl.dataset.id);
      if (footerEl?.dataset?.id)    pageSectionIds.push(footerEl.dataset.id);

      if (pageSectionIds.length) {
        try {
          const secQuery = encodeURIComponent(pageSectionIds.join(','));
          const pageResp = await fetch(`${window.location.pathname}?sections=${secQuery}`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin'
          }).then(r => r.json());

          const replaceSection = (el) => {
            if (!el?.dataset?.id) return;
            const html = pageResp[el.dataset.id];
            if (!html) return;

            const doc = new DOMParser().parseFromString(html, 'text/html');
            const next =
              doc.querySelector(`#${el.id}[data-id="${el.dataset.id}"]`) ||
              doc.getElementById(el.id);

            if (next) el.replaceWith(next);
            else {
              const inner = doc.querySelector('.js-contents');
              if (inner) el.innerHTML = inner.innerHTML;
            }
          };

          if (mainItemsEl) replaceSection(mainItemsEl);
          if (footerEl)    replaceSection(footerEl);
        } catch (e) {
          console.warn('[CartDrawer] section refresh for /cart failed', e);
        }
      }
      */

      // Make sure the right block (empty vs items) shows after any update
      this._syncMainCartEmptyUI(!isEmpty);          // HAS items = !isEmpty
      setTimeout(() => this._syncMainCartEmptyUI(!isEmpty), 0); // belt & suspenders

      // 5) Notify integrations to re-bind (Extend, etc.) ONLY if cart changed
      if (sigChanged && Date.now() - this.__lastExtendNotifiedAt > 1500) {
        this.__lastExtendNotifiedAt = Date.now();
        window.dispatchEvent(new CustomEvent('refreshAjaxSideCart', {
          detail: { by: 'theme', reason, sig: nextSig }
        }));
      }

      // 6) Scroll to last-added if we have a hint
      const variantHint = String(this.__pendingVariantId || window.__lastAddedVariantId || '');
      if (variantHint) {
        setTimeout(() => this.scrollToVariant(variantHint), 40);
        this.__pendingVariantId = '';
        try { window.__lastAddedVariantId = ''; } catch(e) {}
      }

      if (sigChanged) this.__lastSig = nextSig;

      unfreeze();
    } catch (e) {
      console.warn('[CartDrawer] refreshFromServer failed:', e);
    } finally {
      this._coalesceEnd(reason);

      if (typeof window.__extendDrawerShimV3_rehydrate === 'function') {
        setTimeout(() => window.__extendDrawerShimV3_rehydrate('drawer-refresh'), 250);
      }

    }
  }

  open(triggeredBy) {

    this.__justOpenedAt = Date.now();

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Show shell immediately; data hydrates right after
    this.classList.add('animate', 'active');

    // Trigger the ONE central, throttled refresh mechanism.
    if (typeof window.__triggerCartRefreshFromTheme === 'function') {
        window.__triggerCartRefreshFromTheme('drawer:open');
    } else {
        // Fallback for safety, though the global function should exist.
        console.warn('[CartDrawer] Global refresh trigger not found. Using local refresh.');
        if (Date.now() - this.__lastRefreshAt > 800) {
            this.refreshFromServer('open');
        }
    }

    // Trap focus after the opening transition
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        } else {
          console.warn('[CartDrawer] trapFocus is not defined.');
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // (hard lock scroll so the page doesn't move behind the drawer)
    this.__scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${this.__scrollLockY}px`;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    // (restore scroll position)
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.__scrollLockY || 0);

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.id) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Clear "empty" visuals
    this.classList.remove('is-empty');
    this.querySelector('#CartDrawer')?.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    // (ensure the drawer form itself isn't stuck in empty styling)
    const formEl = this.querySelector('#CartDrawer-Form');
    if (formEl) formEl.classList.remove('is-empty');

    // Unhide UI; hide empty message
    const header = this.querySelector('.drawer__header');
    const itemsHost = this.querySelector('cart-drawer-items');
    const footer = this.querySelector('.drawer__footer');
    const emptyContainer = this.querySelector('.drawer__inner-empty');

    if (header) header.style.display = '';
    if (itemsHost) itemsHost.style.display = '';
    if (footer) footer.style.display = '';
    if (emptyContainer) emptyContainer.style.display = 'none';

    this._syncMainCartEmptyUI(true);   // ✅ force “has items” on the add flow

    // Sections from /cart/add
    const sectionsBlob = (parsedState && parsedState.sections) || {};

    // If drawer markup not included (theme variations), open shell and hydrate
    const hasDrawerMarkup = !!(sectionsBlob['cart-drawer'] || sectionsBlob['CartDrawer-CartItems']);
    if (!hasDrawerMarkup) {
      this.classList.add('animate', 'active');
      this.refreshFromServer('renderContents-missing-drawer');
      return;
    }

    // Smooth swap
    const contents = this.querySelector('.drawer__contents');
    const unfreeze = this._freezeHeight(contents);
    if (contents) contents.classList.add('is-swapping');

    // Replace targets
    this.getSectionsToRender().forEach((section) => {
      const sourceHTML = sectionsBlob[section.section] || sectionsBlob[section.id];
      if (!sourceHTML) return;

      const target =
        document.getElementById(section.id) ||
        (section.selector ? document.querySelector(section.selector) : null);
      if (!target) return;

      const doc = new DOMParser().parseFromString(String(sourceHTML), 'text/html');
      const srcNode = section.selector ? doc.querySelector(section.selector) : (doc.body.firstElementChild || doc.body);
      target.innerHTML = srcNode ? srcNode.innerHTML : '';
    });

    // Sync cart page empty UI as well
    this._syncMainCartEmptyUI(false);

    // Finish swap, ensure overlay bound, keep drawer open
    setTimeout(() => {
      this._rebindDrawerInteractions();
      this.classList.add('animate', 'active'); // idempotent

      // Scroll to just-added variant if known
      const variantHint = String(
        (parsedState && (parsedState.id || parsedState.variant_id)) ||
        this.__pendingVariantId ||
        window.__lastAddedVariantId ||
        ''
      );
      if (variantHint) {
        setTimeout(() => this.scrollToVariant(variantHint), 40);
        this.__pendingVariantId = '';
        try { window.__lastAddedVariantId = ''; } catch(e) {}
      }

      // Signal integrations to re-bind
      window.dispatchEvent(new CustomEvent('refreshAjaxSideCart', {
        detail: { by: 'theme', reason: 'drawer-render-after-add' }
      }));

      if (contents) contents.classList.remove('is-swapping');
      unfreeze();
    }, 0);
  }

  getSectionsToRender() {
    const sectionConfigs = [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer-Footer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
    ];

    /*
    // Include main cart items/footer when present so PDP adds can update /cart too
    const mainItemsEl = document.getElementById('main-cart-items');
    if (mainItemsEl?.dataset?.id) {
      sectionConfigs.push({
        id: 'main-cart-items',
        section: mainItemsEl.dataset.id,
        selector: '.js-contents',
      });
    }
    const footerEl = document.getElementById('main-cart-footer');
    if (footerEl?.dataset?.id) {
      sectionConfigs.push({
        id: 'main-cart-footer',
        section: footerEl.dataset.id,
        selector: '.js-contents',
      });
    }

    return sectionConfigs;
    */
  }
  

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    if (selector) {
      const node = doc.querySelector(selector);
      if (node) return node.innerHTML;
    }
    return (doc.body && (doc.body.firstElementChild?.innerHTML || doc.body.innerHTML)) || '';
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(String(html || ''), 'text/html').querySelector(selector);
  }

  // Scroll to the just-added line and briefly highlight it
  scrollToVariant(variantId) {
    if (!variantId) return;

    const root = this.querySelector('.drawer__inner') || this;
    const hit =
      root.querySelector(`input[data-quantity-variant-id="${variantId}"]`) ||
      root.querySelector(`button[data-variant-id="${variantId}"]`) ||
      root.querySelector(`a.cart-item__name[href$="${variantId}"]`);
    if (!hit) return;

    const row = hit.closest('.cart-item') || hit;
    row.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });

    const focusable = row.querySelector('input.quantity__input, button, a');
    if (focusable) focusable.focus({ preventScroll: true });

    if (ENABLE_DRAWER_HIGHLIGHT) {
      row.classList.add('cart-item--just-added');
      setTimeout(() => row.classList.remove('cart-item--just-added'), 1200);

      // Throttle consecutive refreshes so we don't double-swap the drawer
      const REFRESH_THROTTLE_MS = 1200; // was 900/400; a bit higher is smoother
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ----------------- Items wrapper ----------------- */
customElements.whenDefined('cart-items').then(() => {
class CartDrawerItems extends customElements.get('cart-items') {

  __lastSig = '';                 // last cart signature (ids x qty, total)
  __sigChangedThisPass = false;   // set during a refresh pass
  __lastExtendNotifiedAt = 0;     // last time we dispatched refreshAjaxSideCart
  __lastTickAt = 0;               // last time we set cart:tick

  getSectionsToRender() {
    const sectionConfigs = [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer-Footer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
    ];

    // Resolve dynamic cart page sections (when present)
    const mainItemsEl = document.getElementById('main-cart-items');
    if (mainItemsEl?.dataset?.id) {
      sectionConfigs.push({
        id: 'main-cart-items',
        section: mainItemsEl.dataset.id,
        selector: '.js-contents',
      });
    }
    const footerEl = document.getElementById('main-cart-footer');
    if (footerEl?.dataset?.id) {
      sectionConfigs.push({
        id: 'main-cart-footer',
        section: footerEl.dataset.id,
        selector: '.js-contents',
      });
    }

    return sectionConfigs;
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
});
